import { ReactComponent as Arrow } from '$icons/uncolored/arrow.svg';
import { ReactComponent as ArrowWithTail } from '$icons/BackArrow.svg';
import { useEffect, useMemo, useRef, useState } from 'react';
import { GetSharedCandidatesRosterDataOutput } from '$store/modules/candidates';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

export const getScoreColor = (score: number) => {
  const scoresStyles = {
    low: 'bg-[#FCDAC4] text-[#A44101]',
    medium: 'bg-[#CFD0FF] text-[#2C30D7]',
    high: 'bg-[#BFEFEC] text-[#016F68]',
  };

  if (score >= 75) return scoresStyles.high;
  if (score >= 60) return scoresStyles.medium;
  return scoresStyles.low;
};

const SPKSmartScores = ({
  scores,
  jobFits,
  softSkills,
}: {
  scores: { title: string; score: number }[];
  jobFits:
    | NonNullable<
        GetSharedCandidatesRosterDataOutput['candidates'][number]['AIData']
      >['job_fits']
    | undefined;
  softSkills: NonNullable<
    GetSharedCandidatesRosterDataOutput['candidates'][number]['AIData']
  >['soft_skills'];
}) => {
  const [isOpenFits, setIsOpenFits] = useState(true);
  const [isOpenSkillsChart, setIsOpenSkillsChart] = useState(true);
  const chartRef = useRef(null);
  const chartInstance = useRef<Chart>();

  const data = useMemo(() => {
    const { labels, values } = Object.entries(softSkills).reduce(
      (acc, [key, value]) => {
        const label = key.replace(/_/g, ' ');
        acc.labels.push(label.charAt(0).toUpperCase() + label.slice(1));
        acc.values.push(value * 100);
        return acc;
      },
      { labels: [] as string[], values: [] as number[] }
    );

    return {
      labels,
      datasets: [
        {
          label: '',
          data: values,
          backgroundColor: 'rgba(0, 81, 255, 0.08)',
          borderColor: '#0051FF',
          borderWidth: 2,
          pointBackgroundColor: '#0051FF',
        },
      ],
    };
  }, [softSkills]);

  const options = useRef({
    maintainAspectRatio: false,
    layout: {
      padding: {
        left: 10,
        right: 10,
        top: 0,
        bottom: 10,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: 'AI Skill Proficiency',
        font: {
          size: 12,
          weight: 500,
        },
        color: '#6A675A',
        padding: {
          bottom: 20,
        },
      },
      tooltip: {
        enabled: false,
      },
    },
    scales: {
      r: {
        padding: { top: 0 },
        angleLines: {
          display: false,
        },
        grid: {
          color: '#E8E6DB',
        },
        suggestedMin: 0.0,
        suggestedMax: 100,
        ticks: {
          stepSize: 20,
          display: false,
        },
        pointLabels: {
          font: {
            size: 10,
            weight: 500,
          },
          color: '#6A675A',
          backdropColor: '#F3F2EC',
          backdropPadding: {
            top: 6,
            bottom: 6,
            left: 8,
            right: 8,
          },
          borderRadius: 14,
        },
      },
    },
    elements: {
      point: {
        radius: 3,
        backgroundColor: '#96FFC8',
      },
    },
  });

  useEffect(() => {
    if (chartRef.current) {
      chartInstance.current = new Chart(chartRef.current, {
        type: 'radar',
        data,
        options: options.current,
      });
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [data, options]);

  return (
    <div>
      <div className="flex flex-row gap-4 mb-4">
        {scores.map(({ title, score }) => {
          return (
            <div
              className={`${getScoreColor(
                score
              )} p-4 rounded flex flex-col justify-between gap-6 min-w-28`}
              key={`smart_score_${title}`}
            >
              <span className="text-xs">{title}</span>
              <span className="text-[32px] font-black">{score}</span>
            </div>
          );
        })}
      </div>

      <div className="p-4 rounded-xl border border-gray-400 text-sm mb-8 hidden">
        <div className="flex flex-row justify-between">
          <div>Great Fit For</div>
          <button
            type="button"
            className="text-employer-primary flex flex-row items-center gap-2"
            onClick={() => setIsOpenFits((prev) => !prev)}
          >
            See {isOpenFits ? 'Less' : 'More'}
            <Arrow
              className={`fill-employer-primary w-4 h-4 ${
                isOpenFits ? 'rotate-180' : 'rotate-0'
              }`}
            />
          </button>
        </div>

        <div
          className={`text-xs overflow-hidden transition-all duration-500 gap-y-2 flex flex-col ${
            isOpenFits ? 'max-h-96 mt-4' : 'max-h-0'
          }`}
        >
          {jobFits &&
            jobFits.map((singleScore) => {
              return (
                <div
                  key={`jobFits_${singleScore.name}_${singleScore.match_score}`}
                >
                  <div className="flex flex-row items-center">
                    <ArrowWithTail className="fill-employer-primary rotate-180 mr-2" />
                    <span className="font-semibold">{singleScore.name}</span>
                    &nbsp;(
                    {singleScore.match_score * 100}%)
                  </div>
                  <div>{singleScore.reason}</div>
                </div>
              );
            })}
        </div>
      </div>
      <div className="text-sm">
        <div className="flex flex-row justify-between">
          <span className="font-bold">Skills</span>
          <button
            type="button"
            className="text-employer-primary flex flex-row gap-2 items-center"
            onClick={() => setIsOpenSkillsChart((prev) => !prev)}
          >
            Show {isOpenSkillsChart ? 'Less' : 'More'}{' '}
            <Arrow className="fill-employer-primary w-4 h-4 rotate-180" />
          </button>
        </div>
        <div
          className={`transition-all duration-700 overflow-hidden ${
            isOpenSkillsChart ? 'h-96' : 'h-0'
          }`}
        >
          <canvas ref={chartRef} />
        </div>
      </div>
    </div>
  );
};

export default SPKSmartScores;
